import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import ConfirmPopup from "./ConfirmPopup";
import LoadingPopup from "../SharedView/LoadingPopup";
import "./invoicesManagement.css";
import { useZohoOrgId, zohoInvoiceUrl, zohoPaymentUrl } from "../../utils/zohoOrg";

/* =============================================================================
 * InvoiceDrawer — the slide-over invoice detail panel, extracted from
 * InvoicesManagement so the SAME drawer opens on the booking-summary page the
 * moment an invoice is generated (loading shown INSIDE the drawer, then the
 * full details) instead of the old bare LoadingPopup.
 *
 * Self-contained: it fetches its own detail by `invoiceId`, owns its action
 * state (apply / revert / delete / send) and confirm popups, and calls
 * `onChanged` after any mutation so the host can refresh its own list/booking.
 *
 * States:
 *   · creating=true            → "Generating invoice…" spinner (booking page,
 *                                 before the create call returns an id)
 *   · createError set          → the create call failed; show it with a retry
 *   · invoiceId set, no detail → fetching; spinner inside the drawer
 *   · detail loaded            → the full panel
 *
 * The PRIMARY action is unified (2026-07-20): one button that is
 * "Send Invoice" when there is no advance money to apply, and
 * "Apply Payment & Send" when there is (it applies the wallet/advance payment
 * first, then notifies). Revert + Delete stay as secondary actions.
 * ========================================================================== */

const money = (n) =>
  `AED ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone("Asia/Dubai");
  return dt.isValid ? dt.toFormat("dd LLL yyyy") : "—";
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone("Asia/Dubai");
  return dt.isValid ? dt.toFormat("dd LLL yyyy, HH:mm") : "—";
};

// Status is STORED lowercase (so `status === 'paid'` checks stay consistent),
// but DISPLAYED Title Case (2026-07-24).
// Underscores → spaces so "partially_paid" reads "Partially Paid".
const titleCase = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());


/** The wallet amounts still available to apply, cash capped by the balance
 *  after the bonus discount — the SAME math the Apply button used inline. */
/* Returns null — NOT zeros — when the live Zoho read is missing.
 *
 * Returning zeros made hasAdvance false, which silently turned "Apply Payment &
 * Send" into "Send Invoice". The two look identical apart from the label, so an
 * invoice the wallet had already paid went out asking for the full amount. That
 * happened five times to one client, for AED 1,253.70 (2026-09-01). A null says
 * "unknown", and the caller refuses to send rather than guess. */
function computeApplyAmounts(detail) {
  if (!detail?.zoho) return null;
  const walletBonusApplied = parseFloat(detail.invoice.wallet_bonus_applied || 0);
  const walletCashApplied = parseFloat(detail.invoice.wallet_cash_applied || 0);
  const bonusToApply = Math.max(0, (detail.wallet?.bonus_charged || 0) - walletBonusApplied);
  // ADVANCE CARD MONEY COUNTS (owner, 2026-09-03): a direct-rail card payment
  // is advance money exactly like a wallet charge — its Zoho payment sits in
  // the pool — but it lives in card_paid, not cash_charged. Excluding it made
  // hasAdvance false and the button said "Send Invoice" over a booking the
  // client had already paid by card.
  const cardPaid = detail.wallet?.card_paid || 0;
  const walletCash = detail.wallet?.cash_charged || 0;
  const advance = walletCash + cardPaid;
  const cashRemaining = Math.max(0, advance - walletCashApplied);
  const balanceAfterBonus = Math.max(0, detail.zoho.balance - bonusToApply);
  const cashToApply = Math.min(cashRemaining, balanceAfterBonus);
  // What that cash IS, so the dialog can say it (owner 2026-09-23: "say what it
  // actually does"): the server applies the booking's own card payment first,
  // then wallet credit — mirror that order here.
  const cardToApply = Math.min(cashToApply, Math.max(0, cardPaid - walletCashApplied));
  const walletToApply = Math.max(0, cashToApply - cardToApply);
  return { bonusToApply, cashToApply, cardToApply, walletToApply };
}

/** "AED 212.63 advance card payment", "AED 50.00 wallet credit and AED 20.00
 *  bonus discount" — only the parts that are actually there. */
function describeApply({ bonus = 0, card = 0, wallet = 0 }, money) {
  const parts = [];
  if (card > 0.005) parts.push(`${money(card)} advance card payment`);
  if (wallet > 0.005) parts.push(`${money(wallet)} wallet credit`);
  if (bonus > 0.005) parts.push(`${money(bonus)} bonus discount`);
  if (!parts.length) return 'nothing';
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export default function InvoiceDrawer({
  open,
  invoiceId,
  creating = false,
  createError = null,
  canEdit = true, // managers only — supervisors get a read-only drawer
  onClose,
  onChanged,
  onRetryCreate,
  onDeleted, // optional: (bookingIds, deletedInvoiceId) => host patches its list in place (no refetch)
  onSent, // optional: (bookingIds) => host marks those rows "sent" in place (no refetch)
}) {
  const zohoOrgId = useZohoOrgId();
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /* onChanged through a ref, NOT a dependency. Every host passes it as an
     inline arrow, so its identity changes on each render — naming it in the
     detail effect's dep array would re-run the fetch every render, and since
     the fetch can itself call onChanged, that is an endless refetch loop
     against Zoho. The ref keeps the latest callback without re-triggering. */
  const onChangedRef = useRef(onChanged);
  useEffect(() => { onChangedRef.current = onChanged; }, [onChanged]);

  // Round 7 (PLAN_multi_billing.md): shareable HHC pay link — managers and
  // supervisors only (same localStorage role pattern as the host pages).
  const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
  const canSeePayLink = userRole === 'manager' || userRole === 'supervisor';
  const [payLinkUrl, setPayLinkUrl] = useState(null);
  const [payLinkExpiresAt, setPayLinkExpiresAt] = useState(null);
  const [payLinkBusy, setPayLinkBusy] = useState(false);
  const [payLinkError, setPayLinkError] = useState('');
  const [payLinkCopied, setPayLinkCopied] = useState(false);
  const payLinkInputRef = useRef(null);

  const [applyTarget, setApplyTarget] = useState(null);
  const [revertTarget, setRevertTarget] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null); // { applyFirst: bool }

  // Delete: two-stage gate (confirm → type DELETE), same as the invoices page.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStage, setDeleteStage] = useState(null); // null | 'confirm' | 'type'
  const [deleteTyped, setDeleteTyped] = useState("");
  const deleteInputRef = useRef(null);

  const [isActionLoading, setIsActionLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  // Success feedback: when the drawer flips from "generating" to a loaded
  // invoice, flash a brief "created" ribbon (2026-07-20).
  const [showCreatedFlash, setShowCreatedFlash] = useState(false);
  const wasCreatingRef = useRef(false);
  useEffect(() => {
    if (creating) { wasCreatingRef.current = true; return; }
    if (wasCreatingRef.current && invoiceId && detail && !createError) {
      wasCreatingRef.current = false;
      setShowCreatedFlash(true);
      const t = setTimeout(() => setShowCreatedFlash(false), 4500);
      return () => clearTimeout(t);
    }
  }, [creating, invoiceId, detail, createError]);

  // Regenerate (2026-08-09): the drawer swaps itself onto the NEW invoice
  // id after a regenerate — the host's invoiceId prop still points at the old
  // (deleted) one until it refetches.
  const [regenId, setRegenId] = useState(null);
  const effectiveInvoiceId = regenId || invoiceId;
  useEffect(() => { setRegenId(null); }, [invoiceId, open]);

  // Fetch detail whenever the target invoice changes (and the drawer is open).
  useEffect(() => {
    if (!open || !effectiveInvoiceId) { setDetail(null); return; }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const { data } = await axios.get(`/api/invoices/${effectiveInvoiceId}`);
        if (!cancelled) {
          setDetail(data);
          /* OPENING THE DRAWER IS ITSELF A MUTATION (2026-08-30). The detail
             endpoint re-reads Zoho and self-heals the stored status, total,
             paid and balance — so a row the list drew as "Draft" can be "Sent"
             by the time the drawer paints it. Nothing told the host, and the
             chip behind the drawer stayed stale until a full page reload.
             Only fires when the server says it actually wrote something, so a
             drawer open on an already-fresh invoice costs no refetch. */
          if (data?.synced_from_zoho) onChangedRef.current?.();
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, effectiveInvoiceId]);

  // Reset transient action state each time the drawer closes.
  useEffect(() => {
    if (!open) {
      setApplyTarget(null); setRevertTarget(null); setSendConfirm(null);
      setDeleteTarget(null); setDeleteStage(null); setDeleteTyped("");
      setFeedbackMessage("");
    }
  }, [open]);

  // Pay-link state is per-invoice — never carry a minted URL to another invoice.
  useEffect(() => {
    setPayLinkUrl(null); setPayLinkExpiresAt(null); setPayLinkError(''); setPayLinkCopied(false);
  }, [effectiveInvoiceId, open]);

  // Auto-focus the DELETE input once the type-to-confirm stage opens.
  useEffect(() => {
    if (deleteStage === 'type') {
      const t = setTimeout(() => deleteInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [deleteStage]);

  const refreshDetail = useCallback(async () => {
    if (!effectiveInvoiceId) return;
    try {
      const { data: fresh } = await axios.get(`/api/invoices/${effectiveInvoiceId}`);
      setDetail(fresh);
    } catch { /* keep the stale view rather than blanking it */ }
  }, [effectiveInvoiceId]);

  // Regenerate the INVOICE itself (2026-08-09) — manager + supervisor.
  // EDIT-IN-PLACE since 2026-08-17: the invoice keeps its number and its pay
  // link, and only its lines are rewritten from the current booking state.
  // It used to be delete-and-recreate, so the confirm text warned about losing
  // the invoice — that is no longer what happens, and the copy must not say it.
  // WHO DID WHAT (owner, 2026-09-03): the invoice's audit trail — always
  // visible to managers, loaded as soon as the invoice detail is.
  const [historyRows, setHistoryRows] = useState(null);
  useEffect(() => { setHistoryRows(null); }, [effectiveInvoiceId, open]);
  const invoiceIdForHistory = detail?.invoice?.id;
  useEffect(() => {
    if (!open || !invoiceIdForHistory || userRole !== 'manager') return;
    let cancelled = false;
    axios.get(`/api/invoices/${invoiceIdForHistory}/audit`)
      .then((r) => { if (!cancelled) setHistoryRows(Array.isArray(r.data?.entries) ? r.data.entries : []); })
      .catch(() => { if (!cancelled) setHistoryRows([]); });
    return () => { cancelled = true; };
  }, [open, invoiceIdForHistory, userRole]);
  const HISTORY_LABEL = {
    invoice_created: 'Generated',
    invoice_updated: 'Updated from bookings',
    invoice_sent: 'Marked sent',
    invoice_whatsapp_sent: 'WhatsApp notification sent',
    invoice_payment_reverted: 'Payment reverted',
    invoice_paylink_minted: 'Payment link created',
    invoice_deleted: 'Deleted',
  };
  // The applied row says WHERE the money came from — an advance card
  // capture, the wallet, or both (owner, 2026-09-03).
  const appliedLabel = (d) => {
    const card = Number(d?.card_advance || 0);
    const cash = Number(d?.cash || 0);
    if (card > 0.005 && cash > card + 0.005) return 'Advance card + wallet payment applied';
    if (card > 0.005) return 'Advance card payment applied';
    // A wallet settlement says WHO spent the wallet (2026-09-10): the office
    // from the client's wallet page, or the client themselves.
    if (d?.source === 'wallet' && d?.via === 'office') return 'Wallet applied to this invoice by the office';
    if (d?.source === 'wallet' && d?.via === 'client') return 'Paid from wallet by the client';
    return 'Wallet payment applied';
  };

  const [regenBusy, setRegenBusy] = useState(false);
  const [regenError, setRegenError] = useState('');
  useEffect(() => { setRegenError(''); }, [effectiveInvoiceId, open]);
  const regenerateInvoice = async () => {
    const id = detail?.invoice?.id;
    if (!id || regenBusy) return;
    if (!window.confirm('Update this invoice from the current booking details? The invoice keeps its number and payment link — only its lines are rewritten.')) return;
    setRegenBusy(true);
    setRegenError('');
    try {
      const { data } = await axios.post(`/api/invoices/${id}/regenerate`);
      // Normally the id is unchanged (edit in place). It only differs if the
      // Zoho invoice had gone missing and the backend replaced it.
      const newId = data?.invoice?.localInvoiceId || data?.invoice?.id;
      if (newId && newId !== id) setRegenId(newId);
      await refreshDetail();
      onChanged?.();
    } catch (e) {
      setRegenError(e.response?.data?.message || 'Failed to update the invoice.');
    } finally {
      setRegenBusy(false);
    }
  };

  // Regenerate mints a NEW token server-side; the old /pay/<token> dies the
  // moment the row updates (leaked-link revocation, 2026-08-09).
  const regeneratePayLink = async () => {
    const id = detail?.invoice?.id;
    if (!id || payLinkBusy) return;
    if (!window.confirm('Regenerate the payment link? The current link stops working immediately.')) return;
    setPayLinkBusy(true);
    setPayLinkError('');
    try {
      const { data } = await axios.post(`/api/invoices/${id}/pay-link`, { rotate: true });
      setPayLinkUrl(data.page_url);
      setPayLinkExpiresAt(data.expires_at || null);
      setPayLinkCopied(false);
      await refreshDetail();
    } catch (e) {
      setPayLinkError(e.response?.data?.message || 'Failed to regenerate the payment link.');
    } finally {
      setPayLinkBusy(false);
    }
  };

  const createPayLink = async () => {
    const id = detail?.invoice?.id;
    if (!id || payLinkBusy) return;
    setPayLinkBusy(true);
    setPayLinkError('');
    try {
      const { data } = await axios.post(`/api/invoices/${id}/pay-link`);
      setPayLinkUrl(data.page_url);
      setPayLinkExpiresAt(data.expires_at || null);
      // A guest draft flips to "sent" at mint time — refresh so the drawer
      // shows the real status + expiry.
      await refreshDetail();
    } catch (e) {
      console.error('[pay-link] mint failed:', e);
      // 400 draft / 403 role — {message} shown verbatim.
      setPayLinkError(e.response?.data?.message || 'Failed to create the payment link.');
    } finally {
      setPayLinkBusy(false);
    }
  };

  const copyPayLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setPayLinkCopied(true);
    } catch {
      // Clipboard API unavailable (http / permissions) — select + execCommand.
      const el = payLinkInputRef.current;
      if (el) {
        el.select();
        try { document.execCommand('copy'); setPayLinkCopied(true); } catch { /* leave selected for manual copy */ }
      }
    }
    setTimeout(() => setPayLinkCopied(false), 2000);
  };

  const doApplyPayment = async () => {
    if (!applyTarget) return;
    const id = applyTarget.id;
    setApplyTarget(null);
    setIsActionLoading(true);
    setFeedbackMessage("");
    try {
      const res = await axios.post(
        `/api/invoices/${id}/apply-payment`, {},
        { validateStatus: (s) => s >= 200 && s < 500 }
      );
      await refreshDetail();
      onChanged?.();
      setFeedbackMessage(res.data.message || "Payment applied.");
    } catch (e) {
      console.error(e);
      setFeedbackMessage(e.response?.data?.message || "Failed to apply payment.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const doRevertPayment = async () => {
    if (!revertTarget) return;
    const id = revertTarget.id;
    setRevertTarget(null);
    setIsActionLoading(true);
    setFeedbackMessage("");
    try {
      const res = await axios.post(
        `/api/invoices/${id}/revert-payment`, {},
        { validateStatus: (s) => s >= 200 && s < 500 }
      );
      await refreshDetail();
      onChanged?.();
      setFeedbackMessage(res.data.message || "Payment reverted.");
    } catch (e) {
      console.error(e);
      setFeedbackMessage(e.response?.data?.message || "Failed to revert payment.");
    } finally {
      setIsActionLoading(false);
    }
  };

  // Open the send confirm, previewing the EXACT total the client will be told.
  // Fetches the client's current pending-invoice total (drafts excluded) and
  // folds in THIS invoice's post-action balance: for a draft it is added (the
  // send makes it count); "Apply Payment & Send" subtracts the advance money
  // that will settle against it. The /send endpoint recomputes authoritatively,
  // so this is a faithful preview, never the source of truth.
  const openSendConfirm = async (applyFirst) => {
    const id = detail?.invoice?.id;
    if (!id) return;
    // Already sent? Anything past "draft" in Zoho means this is a RE-send —
    // warn before re-notifying the client (2026-07-20).
    const zStatus = String(detail?.zoho?.status || '').toLowerCase();
    const alreadySent = !!zStatus && zStatus !== 'draft';
    setSendConfirm({ applyFirst, loading: true, previewDue: null, alreadySent });
    const a = computeApplyAmounts(detail) || { bonusToApply: 0, cashToApply: 0, cardToApply: 0, walletToApply: 0 };
    try {
      const { data } = await axios.get(`/api/invoices/${id}/send-preview`);
      const currentTotal = Number(data.current_total_due) || 0;
      const applied = applyFirst ? (a.bonusToApply + a.cashToApply) : 0;
      const isDraft = zStatus === 'draft';
      const thisBal = Number(detail?.zoho?.balance) || 0;
      const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
      const due = isDraft
        // Not yet in the current total — sending adds it (minus any payment applied).
        ? r2(currentTotal + Math.max(0, thisBal - applied))
        // Already counted — applying reduces this invoice's share of the total.
        : r2(Math.max(0, currentTotal - Math.min(thisBal, applied)));
      setSendConfirm({ applyFirst, loading: false, previewDue: due, alreadySent, applyWhat: describeApply({ bonus: a.bonusToApply, card: a.cardToApply || 0, wallet: a.walletToApply ?? a.cashToApply }, money) });
    } catch (e) {
      // Preview failed (e.g. Zoho) — still let them send, just without the figure.
      setSendConfirm({ applyFirst, loading: false, previewDue: null, alreadySent, applyWhat: describeApply({ bonus: a.bonusToApply, card: a.cardToApply || 0, wallet: a.walletToApply ?? a.cashToApply }, money) });
    }
  };

  // UNIFIED SEND: optionally apply advance payment first, then notify the client.
  const doSend = async (applyFirst) => {
    const id = detail?.invoice?.id;
    setSendConfirm(null);
    if (!id) return;
    setIsActionLoading(true);
    setFeedbackMessage("");
    try {
      if (applyFirst) {
        // Apply the advance/wallet payment. A 400 "nothing to apply" is not a
        // failure here — it just means it was already applied; still send.
        await axios.post(
          `/api/invoices/${id}/apply-payment`, {},
          { validateStatus: (s) => s >= 200 && s < 500 }
        );
      }
      const res = await axios.post(
        `/api/invoices/${id}/send`, {},
        { validateStatus: (s) => s >= 200 && s < 500 }
      );
      await refreshDetail();
      // Prefer an in-place patch (no full reload) when the host supports it —
      // pass the resulting status ('sent' | 'paid' | …) so the badge is accurate.
      const sentBookingIds = Array.isArray(detail?.bookings) ? detail.bookings.map((b) => b.id) : [];
      if (onSent) onSent(sentBookingIds, res.data?.invoice_status || 'sent');
      else onChanged?.();
      setFeedbackMessage(res.data.message || "Invoice sent.");
    } catch (e) {
      console.error(e);
      setFeedbackMessage(e.response?.data?.message || "Failed to send the invoice.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const doDelete = async (force = false) => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    // Bookings that were on this invoice — captured before delete so a host can
    // patch them back to "not invoiced" in place instead of refetching.
    const affectedBookingIds = Array.isArray(detail?.bookings)
      ? detail.bookings.map((b) => b.id)
      : [];
    setDeleteStage(null); setDeleteTarget(null); setDeleteTyped("");
    setIsActionLoading(true);
    setFeedbackMessage("");
    try {
      const { data } = await axios.delete(`/api/invoices/${id}`, { data: { force } });
      // Prefer an in-place patch (no full-page reload) when the host supports it;
      // otherwise fall back to the host's refetch.
      // Pass the ACTUAL deleted id — after a regenerate the drawer is on a
      // newer invoice than the host's invoiceId prop (audit 2026-08-09).
      if (onDeleted) onDeleted(affectedBookingIds, detail?.invoice?.id);
      else onChanged?.();
      // The invoice is gone — nothing left to show; close the drawer.
      setFeedbackMessage(`Deleted. ${data.bookings_unlinked || 0} booking(s) unlinked.`);
      onClose?.();
    } catch (e) {
      console.error(e);
      setFeedbackMessage(e.response?.data?.message || "Failed to delete invoice.");
    } finally {
      setIsActionLoading(false);
    }
  };

  if (!open) return null;

  const showSpinner = creating || detailLoading || (!!invoiceId && !detail && !createError);
  const spinnerText = creating ? "Generating invoice…" : "Loading…";
  const apply = computeApplyAmounts(detail);
  // apply === null means Zoho did not load, so we cannot know what to apply.
  const applyUnknown = apply === null;
  const hasAdvance = !applyUnknown && (apply.bonusToApply > 0.005 || apply.cashToApply > 0.005);
  // Wallet money already taken for these bookings but not yet on the invoice.
  // The server refuses such a send; the button must not invite it either.
  const walletUnapplied = Math.round((
    Math.max(0, (detail?.wallet?.cash_charged || 0) - parseFloat(detail?.invoice?.wallet_cash_applied || 0))
    + Math.max(0, (detail?.wallet?.bonus_charged || 0) - parseFloat(detail?.invoice?.wallet_bonus_applied || 0))
  ) * 100) / 100;
  const alreadyApplied =
    parseFloat(detail?.invoice?.wallet_bonus_applied || 0) > 0 ||
    parseFloat(detail?.invoice?.wallet_cash_applied || 0) > 0;

  return (
    <div className="invMgr__drawerOverlay" onClick={onClose}>
      <div className="invMgr__drawer" onClick={(e) => e.stopPropagation()}>
        <div className="invMgr__drawerHeader">
          <h2 className="invMgr__drawerTitle">
            {creating
              ? "Generating invoice…"
              : (detail?.invoice?.zoho_invoice_number || (effectiveInvoiceId ? `Invoice #${effectiveInvoiceId}` : "Invoice"))}
          </h2>
          <button className="modal-x" onClick={onClose} />
        </div>

        {showSpinner ? (
          <div className="invMgr__loading">
            <div className="app-loading"><div className="app-spinner" /><div className="app-spinner-text">{spinnerText}</div></div>
          </div>
        ) : createError ? (
          <div className="invMgr__drawerBody">
            <div className="invMgr__empty">
              <div className="invMgr__emptyTitle">Couldn’t generate the invoice</div>
              <div className="invMgr__emptyText">{createError}</div>
              {onRetryCreate && (
                <button className="invMgr__btn invMgr__btn--primary" style={{ marginTop: 12 }} onClick={onRetryCreate}>
                  Try again
                </button>
              )}
            </div>
          </div>
        ) : !detail ? (
          <div className="invMgr__drawerBody">
            <div className="invMgr__empty">
              <div className="invMgr__emptyTitle">Couldn’t load this invoice</div>
              <div className="invMgr__emptyText">Please close and try again.</div>
            </div>
          </div>
        ) : (
          <div className="invMgr__drawerBody">
            {showCreatedFlash && (
              <div className="invMgr__createdFlash" role="status">
                <span className="invMgr__createdFlashCheck">✓</span>
                Invoice created successfully
              </div>
            )}

            {/* DELETED IN ZOHO (2026-08-17). Before this, an invoice whose Zoho
                record had been deleted looked completely normal here — the only
                symptom was a 404 in the server log. It sits at the very top
                because every figure below it is a stale local copy of a record
                that no longer exists. */}
            {detail.zoho_deleted && (
              <div className="invMgr__zohoGone" role="alert">
                <div className="invMgr__zohoGoneTitle">
                  This invoice no longer exists in Zoho Books
                </div>
                <div className="invMgr__zohoGoneText">
                  {detail.invoice.zoho_invoice_number
                    ? <>Invoice <strong>{detail.invoice.zoho_invoice_number}</strong> was deleted in Zoho</>
                    : <>The linked Zoho invoice was deleted</>}
                  {detail.zoho_deleted_at ? <> — noticed {fmtDT(detail.zoho_deleted_at)}</> : null}.
                  {' '}The figures below are the portal’s last known copy and are no longer backed by Zoho.
                  Delete this invoice here, then raise a new one from Bookings → Booking Summary.
                </div>
              </div>
            )}

            {/* Zoho was unreachable on this load — explicitly NOT the same as
                deleted, and must never be shown as if it were. */}
            {detail.zoho_unreachable && (
              <div className="invMgr__zohoStale" role="status">
                Couldn’t reach Zoho Books just now, so the live status below may be out of date.
              </div>
            )}

            {/* Top info grid */}
            <div className="invMgr__grid">
              <div>
                <span className="invMgr__lbl">Client</span>
                <span className="invMgr__val">
                  {/* The name opens the client (2026-08-30) — the drawer already
                      links out to the bookings and to Zoho, and the client was
                      the one thing you had to go and find by hand.
                      The anchor carries invMgr__link ALONE: .invMgr__val sets
                      color on the parent span, and adding both classes to the
                      same element would lose on source order the way the Zoho
                      link did before it was given a doubled class. */}
                  {detail.invoice.client_id ? (
                    <a className="invMgr__link" href={`/clients/${detail.invoice.client_id}`}>
                      {detail.invoice.client_name}
                    </a>
                  ) : detail.invoice.client_name}
                  {/* Guest invoice (Round 7): billed to a one-time guest
                      customer, not the client's own billing. */}
                  {detail.invoice.is_guest_invoice && (
                    <span className="invMgr__guestChip" title="One-time guest customer — not linked to the client's billing">
                      Guest: {detail.invoice.guest_name || '—'}
                    </span>
                  )}
                </span>
              </div>
              {/* Multi-billing: only present when the invoice bills a non-default entity */}
              {detail.invoice.billing_entity_label && (
                <div><span className="invMgr__lbl">Billed to</span><span className="invMgr__val">{detail.invoice.billing_entity_label}</span></div>
              )}
              <div><span className="invMgr__lbl">Created</span><span className="invMgr__val">{fmtDT(detail.invoice.created_at)}</span></div>
              <div><span className="invMgr__lbl">Due</span><span className="invMgr__val">{fmtDate(detail.invoice.due_date)}</span></div>
              <div><span className="invMgr__lbl">Local Total</span><span className="invMgr__val">{money(detail.invoice.total_amount)}</span></div>
              <div><span className="invMgr__lbl">Status</span><span className="invMgr__val">{titleCase(detail.invoice.status)}</span></div>
              {detail.invoice.client_notified_at && (
                <div><span className="invMgr__lbl">Client notified</span><span className="invMgr__val">{fmtDT(detail.invoice.client_notified_at)}</span></div>
              )}
              {detail.invoice.zoho_invoice_id && zohoOrgId && (
                <div>
                  <span className="invMgr__lbl">Zoho</span>
                  <a className="invMgr__val invMgr__link invMgr__link--zoho"
                    href={zohoInvoiceUrl(zohoOrgId, detail.invoice.zoho_invoice_id)}
                    target="_blank" rel="noopener noreferrer">
                    Open in Zoho Books
                  </a>
                </div>
              )}
            </div>

            {/* Zoho live state */}
            {detail.zoho && (
              <div className="invMgr__section">
                <div className="invMgr__sectionTitle">Zoho Books Status</div>
                <div className="invMgr__grid">
                  <div><span className="invMgr__lbl">Status</span><span className="invMgr__val">{titleCase(detail.zoho.status)}</span></div>
                  <div><span className="invMgr__lbl">Total</span><span className="invMgr__val">{money(detail.zoho.total)}</span></div>
                  <div><span className="invMgr__lbl">Balance</span><span className="invMgr__val">{money(detail.zoho.balance)}</span></div>
                  <div><span className="invMgr__lbl">Paid</span><span className="invMgr__val">{money(detail.zoho.payment_made)}</span></div>
                  {detail.zoho.discount > 0 && (
                    <div><span className="invMgr__lbl">Discount (bonus)</span><span className="invMgr__val">{money(detail.zoho.discount)}</span></div>
                  )}
                </div>
              </div>
            )}

            {/* Transactions — card payments (receipts) + wallet activity */}
            {(() => {
              const w = detail.wallet || {};
              const receipts = Array.isArray(detail.receipts) ? detail.receipts : [];
              const walletTxns = Array.isArray(w.transactions) ? w.transactions : [];
              const cardPaid = Number(
                w.card_paid != null ? w.card_paid : receipts.reduce((s, r) => s + Number(r.amount || 0), 0)
              );
              const linkPaid = (detail.pay_link_payments || [])
                .reduce((sum, p) => sum + Number(p.amount || 0), 0);
              const guestLink = (detail.pay_link_payments || []).length > 0
                && !!detail.invoice.is_guest_invoice;
              // Settlements applied to this invoice (2026-09-10): wallet applied
              // by the office or the client, a card settlement, a bank transfer.
              const settlements = Array.isArray(detail.settlements) ? detail.settlements : [];
              const settledBy = (src) => settlements
                .filter((s) => s.source === src)
                .reduce((sum, s) => sum + Number(s.amount || 0), 0);
              const settleLabel = (s) => (
                s.source === 'wallet' ? (s.by_office ? 'Wallet applied by the office' : 'Paid from wallet')
                  : s.source === 'card' ? 'Card settlement'
                    : s.source === 'bank_transfer' ? 'Bank transfer'
                      : s.source === 'cash' ? 'Cash payment' : 'Settlement');

              const zBal = detail.zoho?.balance;
              const outstanding = Number(
                zBal != null ? zBal : (detail.invoice.balance != null
                  ? detail.invoice.balance
                  : detail.invoice.total_amount) || 0
              );

              // One card per rail that actually collected something.
              const cards = [
                { key: 'cash', label: 'Wallet cash charged', amount: Number(w.cash_charged || 0) },
                { key: 'bonus', label: 'Wallet bonus charged', amount: Number(w.bonus_charged || 0) },
                { key: 'card', label: 'Advance card payment', amount: cardPaid },
                { key: 'link', label: guestLink ? 'Payment link (guest)' : 'Payment link', amount: linkPaid },
                { key: 'wallet_settle', label: 'Wallet applied', amount: settledBy('wallet') },
                { key: 'card_settle', label: 'Card settlement', amount: settledBy('card') },
                { key: 'bank_settle', label: 'Bank transfer', amount: settledBy('bank_transfer') },
                { key: 'cash_settle', label: 'Cash payment', amount: settledBy('cash') },
              ].filter((c) => c.amount > 0.009);
              // What is STILL OWED sits beside what was collected (owner,
              // 2026-09-10: "next to wallet applied show pending too") — only
              // once something has been paid; an untouched invoice keeps the
              // "No transaction yet" state, which already carries the figure.
              if (cards.length > 0 && outstanding > 0.009) {
                cards.push({ key: 'pending', label: 'Pending', amount: outstanding });
              }
              // Nothing on any rail, yet the invoice is settled — bank transfer,
              // cash, or a consolidated invoice paid in one go.
              const paidOnInvoiceOnly = cards.length === 0 && outstanding <= 0.009;
              return (
                <div className="invMgr__section">
                  <div className="invMgr__sectionTitle">Transactions</div>
                  {/* ONLY WHAT APPLIES (2026-08-30): "no need list each option
                      just list the one which was applicable". Cash Charged,
                      Bonus Used and Card Paid used to render side by side on
                      every invoice, three of them reading AED 0.00 — which said
                      nothing, and on a link-paid invoice said it three times.
                      A card appears here only if that rail actually moved money;
                      if none did, the state itself is the message. */}
                  {cards.length > 0 ? (
                    <div className="invMgr__walletCards">
                      {cards.map((c) => (
                        <div key={c.key} className={`invMgr__walletCard invMgr__walletCard--${c.key}`}>
                          <div className="invMgr__walletLbl">{c.label}</div>
                          <div className="invMgr__walletVal">{money(c.amount)}</div>
                        </div>
                      ))}
                    </div>
                  ) : paidOnInvoiceOnly ? (
                    <div className="invMgr__txnState invMgr__txnState--paid">
                      <strong>Paid by invoice</strong>
                      <span>
                        Settled against {detail.invoice.zoho_invoice_number || 'this invoice'} —
                        no wallet, card or link payment is recorded against it.
                      </span>
                    </div>
                  ) : (
                    <div className="invMgr__txnState invMgr__txnState--none">
                      <strong>No transaction yet</strong>
                      <span>{money(outstanding)} outstanding on this invoice.</span>
                    </div>
                  )}

                  {receipts.length > 0 && (
                    <div className="invMgr__txnList">
                      {receipts.map(r => (
                        <div key={`rcpt-${r.id}`} className="invMgr__txnRow invMgr__txnRow--receipt">
                          <span className="invMgr__txnDate">{fmtDT(r.gateway_captured_at || r.created_at)}</span>
                          <span className="invMgr__txnDesc invMgr__txnDesc--card">Card payment · {money(r.amount)}</span>
                          <span className="invMgr__txnType invMgr__txnType--card">Card</span>
                          <span className="invMgr__txnAmt">
                            {r.zoho_payment_id && zohoOrgId ? (
                              <a className="invMgr__link"
                                href={zohoPaymentUrl(zohoOrgId, r.zoho_payment_id)}
                                target="_blank" rel="noopener noreferrer">
                                Receipt
                              </a>
                            ) : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {(detail.pay_link_payments || []).length > 0 && (
                    <div className="invMgr__linkTxnList">
                      {(detail.pay_link_payments || []).map((p) => (
                        // Its own two-line layout rather than the 5-column txn
                        // grid, which strands the code and the receipt link on
                        // separate lines at drawer width.
                        <div key={`lnk-${p.id}`} className="invMgr__linkTxn">
                          <div className="invMgr__linkTxnTop">
                            <span className="invMgr__linkTxnDesc">Payment link</span>
                            <span className="invMgr__linkTxnAmt">{money(p.amount)}</span>
                          </div>
                          <div className="invMgr__linkTxnMeta">
                            <span>{fmtDT(p.paid_at)}</span>
                            <span className="invMgr__linkTxnCode">{p.payment_code}</span>
                            {p.merchant_reference && (
                              <span className="invMgr__linkTxnCode">{p.merchant_reference}</span>
                            )}
                            {p.zoho_payment_id && zohoOrgId ? (
                              <a className="invMgr__link"
                                href={zohoPaymentUrl(zohoOrgId, p.zoho_payment_id)}
                                target="_blank" rel="noopener noreferrer">
                                Receipt
                              </a>
                            ) : (
                              <span className="invMgr__linkTxnNoReceipt">Receipt id not stored yet</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {settlements.length > 0 && (
                    <div className="invMgr__linkTxnList">
                      {settlements.map((s) => (
                        <div key={`stl-${s.id}`} className={`invMgr__linkTxn invMgr__settleTxn invMgr__settleTxn--${s.source}`}>
                          <div className="invMgr__linkTxnTop">
                            <span className="invMgr__linkTxnDesc">{settleLabel(s)}{s.by_name ? ` · ${s.by_name}` : ''}</span>
                            <span className="invMgr__linkTxnAmt">{money(s.amount)}</span>
                          </div>
                          <div className="invMgr__linkTxnMeta">
                            <span>{fmtDT(s.settled_at)}</span>
                            <span className="invMgr__linkTxnCode">{s.payment_code}</span>
                            {s.zoho_payment_id && zohoOrgId ? (
                              <a className="invMgr__link"
                                href={zohoPaymentUrl(zohoOrgId, s.zoho_payment_id)}
                                target="_blank" rel="noopener noreferrer">
                                Receipt
                              </a>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {walletTxns.length > 0 && (
                    <div className="invMgr__txnList">
                      {walletTxns.map(t => (
                        <div key={t.id} className="invMgr__txnRow">
                          <span className="invMgr__txnDate">{fmtDT(t.created_at)}</span>
                          <span className="invMgr__txnDesc">{t.description}</span>
                          <span className={`invMgr__txnType invMgr__txnType--${t.type}`}>{t.type === 'booking_charge' ? 'Wallet' : 'Refund'}</span>
                          <span className="invMgr__txnAmt">{money(Math.abs(t.cash_amount || 0))} cash</span>
                          <span className="invMgr__txnAmt">{money(Math.abs(t.bonus_amount || 0))} bonus</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Bookings */}
            <div className="invMgr__section">
              <div className="invMgr__sectionTitle">Bookings on this Invoice ({detail.bookings.length})</div>
              <div className="invMgr__bookingList">
                {detail.bookings.map(b => (
                  <a key={b.id} href={`/bookings/${b.id}`} className="invMgr__bookingLink">
                    <span className="invMgr__bookingId">#{b.id}</span>
                    <span>{fmtDT(b.booking_start)}</span>
                    <span>{b.booking_type}</span>
                    <span className="invMgr__bookingStatus">{b.booking_status}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Payment Preview */}
            {detail.zoho && (detail.wallet.bonus_charged > 0 || detail.wallet.cash_charged > 0) && (() => {
              const zohoTotal = detail.zoho.total;
              const zohoBalance = detail.zoho.balance;
              const existingDiscount = detail.zoho.discount || 0;
              const { bonusToApply, cashToApply } = apply;
              const cashRemaining = Math.max(0, detail.wallet.cash_charged - parseFloat(detail.invoice.wallet_cash_applied || 0));
              const balanceAfterBonus = Math.max(0, zohoBalance - bonusToApply);
              const totalAfterDiscount = zohoTotal - (existingDiscount + bonusToApply);
              const finalBalance = Math.max(0, balanceAfterBonus - cashToApply);
              const nothingToDo = bonusToApply === 0 && cashToApply === 0;
              /* APPLIED = SAY SO IN GREEN (owner, 2026-09-02: the done state
                 looked identical to the preview, ending on a muted "already
                 applied" footnote). Once everything is applied the preview
                 arithmetic is history — swap the table for the same success
                 panel the paid payment link uses. */
              const appliedCash = parseFloat(detail.invoice.wallet_cash_applied || 0);
              const appliedBonus = parseFloat(detail.invoice.wallet_bonus_applied || 0);
              const appliedTotal = appliedCash + appliedBonus;
              if (nothingToDo && appliedTotal > 0.005) {
                /* Green ONLY when the story is over (owner, 2026-09-02: "not
                   clear enough" — a green tick over money still owed read as
                   done). A remaining balance keeps the panel amber and leads
                   with what is still to collect. */
                const settledInFull = zohoBalance <= 0.009;
                return (
                  <div className="invMgr__section invMgr__payPreview">
                    <div className="invMgr__sectionTitle">Wallet Payment</div>
                    <div className={settledInFull ? 'invMgr__payLinkPaid' : 'invMgr__payLinkPaid invMgr__payLinkPaid--partial'}>
                      <div className="invMgr__payLinkPaidHead">
                        <span className={settledInFull ? 'invMgr__payLinkPaidTick' : 'invMgr__payLinkPaidTick invMgr__payLinkPaidTick--partial'} aria-hidden="true">
                          {settledInFull ? '✓' : '!'}
                        </span>
                        <span className="invMgr__payLinkPaidTitle">
                          {settledInFull
                            ? 'Wallet payment applied to this invoice'
                            : `${money(zohoBalance)} still to collect on this invoice`}
                        </span>
                      </div>
                      <div className="invMgr__payLinkPaidCapture">
                        <div className="invMgr__payLinkPaidRow">
                          <span className="invMgr__payLinkPaidAmt">{money(appliedTotal)}</span>
                          <span className="invMgr__payLinkPaidSep">·</span>
                          <span>
                            {appliedBonus > 0.005
                              ? `${money(appliedCash)} cash + ${money(appliedBonus)} bonus applied`
                              : 'applied from the client’s wallet'}
                          </span>
                        </div>
                        <div className={settledInFull ? 'invMgr__appliedNote' : 'invMgr__appliedNote invMgr__appliedNote--partial'}>
                          {settledInFull
                            ? 'The invoice is fully paid — nothing left to collect.'
                            : `The wallet covered ${money(appliedTotal)} of ${money(zohoTotal)} — the remaining ${money(zohoBalance)} is on the invoice balance.`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div className="invMgr__section invMgr__payPreview">
                  <div className="invMgr__sectionTitle">Payment Preview</div>
                  <div className="invMgr__payRow"><span>Invoice total</span><span>{money(zohoTotal)}</span></div>
                  {existingDiscount > 0 && (
                    <div className="invMgr__payRow invMgr__payRow--discount"><span>Existing discount</span><span>−{money(existingDiscount)}</span></div>
                  )}
                  {bonusToApply > 0 && (
                    <div className="invMgr__payRow invMgr__payRow--discount"><span>Bonus discount to apply</span><span>−{money(bonusToApply)}</span></div>
                  )}
                  <div className="invMgr__payRow invMgr__payRow--subtotal"><span>After discount</span><span>{money(totalAfterDiscount)}</span></div>
                  {parseFloat(detail.invoice.wallet_cash_applied || 0) > 0.005 && (
                    <div className="invMgr__payRow invMgr__payRow--cash">
                      <span>Wallet already applied</span>
                      <span>−{money(parseFloat(detail.invoice.wallet_cash_applied || 0))}</span>
                    </div>
                  )}
                  {cashToApply > 0 && (
                    <div className="invMgr__payRow invMgr__payRow--cash"><span>Cash to apply from excess payments</span><span>−{money(cashToApply)}</span></div>
                  )}
                  <div className="invMgr__payRow invMgr__payRow--final"><span>Balance after applying</span><span>{money(finalBalance)}</span></div>
                  {cashRemaining > cashToApply + 0.005 && (
                    <div className="invMgr__payNote">Note: {money(cashRemaining - cashToApply)} of cash available won't be applied (invoice balance is lower).</div>
                  )}
                  {nothingToDo && (<div className="invMgr__payNote">All wallet amounts already applied.</div>)}
                </div>
              );
            })()}

            {/* Payment link (Round 7) — managers + supervisors. A shareable
                HHC /pay/ page: invoice + PDF + card checkout. Drafts can't be
                paid, so minting is blocked until the invoice is sent. */}
            {/* SINGLE-booking invoices only (2026-08-09) — the server
                enforces the same rule at mint time. */}
            {canSeePayLink && (detail.bookings?.length ?? 0) <= 1 && (() => {
              const token = detail.invoice.public_pay_token;
              const link = payLinkUrl || (token ? `${window.location.origin}/pay/${token}` : null);
              const invStatus = String(detail.invoice.status || '').toLowerCase();
              const isDraftInv = invStatus === 'draft';
              const zohoBalance = detail.zoho?.balance;
              /* NOTHING OWED → NO LINK (owner, 2026-09-02: the drawer offered
                 "Create payment link" on an invoice with a 0 pending balance).
                 The server already refuses that mint using the portal row's
                 balance — mirror it here, because detail.zoho can be missing
                 or stale and the old guard trusted it alone. */
              const portalOpen = detail.invoice.balance == null
                ? Number(detail.invoice.total_amount)
                : Number(detail.invoice.balance);
              const isVoidInv = invStatus === 'void';
              const isPaidInv = invStatus === 'paid'
                || (zohoBalance != null && Number(zohoBalance) <= 0.009)
                || (Number.isFinite(portalOpen) && portalOpen <= 0.009);
              /* WALLET-COVERED = NOTHING TO COLLECT (owner, 2026-09-02). A
                 wallet-charged booking's invoice shows a balance until Apply
                 Payment runs, but the client owes nothing — a link here
                 would collect the money a second time. Same null-means-
                 unknown contract as the send button: no Zoho read → no
                 verdict, the link stays available. */
              const applyAmts = computeApplyAmounts(detail);
              const walletCovered = !isPaidInv && applyAmts != null
                && (Number(zohoBalance ?? portalOpen) - applyAmts.bonusToApply - applyAmts.cashToApply) <= 0.009;
              // 7-day validity (2026-08-09) — the mint response wins over
              // the (possibly stale) detail row.
              const expiresAt = payLinkExpiresAt || detail.invoice.pay_link_expires_at || null;
              const linkExpired = expiresAt && new Date(expiresAt).getTime() < Date.now();
              // Captures actually collected THROUGH this link (2026-08-30).
              // The pay-by-link rail writes no receipt row, so before this the
              // drawer could not tell "paid by the link" from "paid some other
              // way" and said the same misleading thing for both.
              const linkPayments = detail.pay_link_payments || [];
              const paidByLink = isPaidInv && linkPayments.length > 0;
              return (
                <div className="invMgr__section invMgr__payLink">
                  <div className="invMgr__sectionTitle">Payment Link</div>
                  {paidByLink ? (
                    // THIS invoice was settled by THIS link. Say so, and keep
                    // the link itself — it is the record of how the money came
                    // in, and support gets asked for it.
                    <div className="invMgr__payLinkPaid">
                      <div className="invMgr__payLinkPaidHead">
                        <span className="invMgr__payLinkPaidTick" aria-hidden="true">✓</span>
                        <span className="invMgr__payLinkPaidTitle">
                          Payment Link was paid
                        </span>
                      </div>
                      {linkPayments.map((p) => (
                        <div key={p.id} className="invMgr__payLinkPaidCapture">
                          {/* Amount + when on one line, the codes on their own.
                              Dot separators between wrapping chips leave a
                              dangling "·" at the end of a line in a narrow
                              drawer — the codes are chips, so they need none. */}
                          <div className="invMgr__payLinkPaidRow">
                            <span className="invMgr__payLinkPaidAmt">{money(p.amount)}</span>
                            <span className="invMgr__payLinkPaidSep">·</span>
                            <span>{fmtDT(p.paid_at)}</span>
                          </div>
                          <div className="invMgr__payLinkPaidCodes">
                            <span className="invMgr__payLinkPaidCode">{p.payment_code}</span>
                            {p.merchant_reference && (
                              <span className="invMgr__payLinkPaidRef">{p.merchant_reference}</span>
                            )}
                            {p.zoho_payment_id && (
                              <span className="invMgr__payLinkPaidRef" title="Zoho payment id">
                                Zoho {p.zoho_payment_id}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      {link && (
                        <div className="invMgr__payLinkRow">
                          <input
                            ref={payLinkInputRef}
                            className="invMgr__payLinkInput"
                            readOnly
                            value={link}
                            onFocus={(e) => e.target.select()}
                          />
                          <button
                            type="button"
                            className="invMgr__btn invMgr__btn--ghost"
                            onClick={() => copyPayLink(link)}
                          >
                            {payLinkCopied ? 'Copied ✓' : 'Copy'}
                          </button>
                        </div>
                      )}
                      <div className="invMgr__payNote">
                        The link that collected this payment, kept for the record.
                        It can no longer be paid — the invoice has no open balance.
                      </div>
                    </div>
                  ) : isVoidInv ? (
                    <div className="invMgr__payNote">This invoice is void — no payment link.</div>
                  ) : walletCovered ? (
                    <div className="invMgr__payNote">
                      The client&rsquo;s wallet already covers this invoice — use Apply Payment
                      instead. A payment link would collect the money a second time.
                    </div>
                  ) : isPaidInv ? (
                    // Paid or zero-balance invoices never mint or regenerate a
                    // link — even an existing one is pointless now.
                    <div className="invMgr__payNote">This invoice has no open balance — no payment link needed.</div>
                  ) : link && !linkExpired ? (
                    <div className="invMgr__payLinkRow">
                      <input
                        ref={payLinkInputRef}
                        className="invMgr__payLinkInput"
                        readOnly
                        value={link}
                        onFocus={(e) => e.target.select()}
                      />
                      <button
                        type="button"
                        className="invMgr__btn invMgr__btn--ghost"
                        onClick={() => copyPayLink(link)}
                      >
                        {payLinkCopied ? 'Copied ✓' : 'Copy'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="invMgr__btn invMgr__btn--primary"
                      disabled={payLinkBusy}
                      title={isDraftInv
                        ? 'Creates the link and marks the invoice as sent — no WhatsApp message is sent'
                        : undefined}
                      onClick={createPayLink}
                    >
                      {payLinkBusy ? 'Creating…' : (linkExpired ? 'Create new payment link' : 'Create payment link')}
                    </button>
                  )}
                  {expiresAt && !isPaidInv && (
                    <div className={`invMgr__payLinkValidity${linkExpired ? ' is-expired' : ''}`}>
                      {linkExpired
                        ? `Expired ${fmtDate(expiresAt)} — the invoice is no longer payable by this link.`
                        : `Valid until ${fmtDate(expiresAt)}`}
                    </div>
                  )}
                  {!!payLinkError && <div className="invMgr__payLinkError">{payLinkError}</div>}
                  {/* Sharing instructions belong only where there is something
                      to share. This ran unconditionally, so a PAID invoice was
                      told "Share this link … pays by card" directly under
                      "no payment link needed" (2026-08-30). */}
                  {!isPaidInv && (
                    <div className="invMgr__payNote">
                      Share this link — the guest sees the invoice + PDF and pays by card.
                      {isDraftInv ? ' Creating the link marks this draft as sent (no WhatsApp is sent).' : ''}
                      {' '}Links are valid for 7 days; each payment attempt opens a fresh secure checkout.
                    </div>
                  )}
                  {/* Regenerate sits BELOW the note, quiet and separate from
                      Copy (2026-08-09) — manager/supervisor only (this
                      whole section already is). */}
                  {link && !isPaidInv && (
                    <button
                      type="button"
                      className="invMgr__payLinkRegen"
                      disabled={payLinkBusy}
                      title="Mints a NEW link and kills the current one immediately — use if the link leaked"
                      onClick={regeneratePayLink}
                    >
                      {payLinkBusy ? 'Regenerating…' : 'Regenerate link'}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Actions — send/apply/revert/delete stay MANAGER-ONLY (owner
                2026-07-20). Regenerate Invoice = manager + supervisor (the owner
                2026-08-09), hidden once money is on the invoice. */}
            {(() => {
              const invStatus = String(detail.invoice.status || '').toLowerCase();
              // Anything that is not a draft has been issued to the client
              // already — Zoho's status is the source of truth when we have it.
              const zSt = String(detail.zoho?.status || '').toLowerCase();
              const invoiceLeftDraft = (zSt || invStatus) !== 'draft' && !!(zSt || invStatus);
              // Supervisors can only regenerate SINGLE-booking invoices (the
              // server 403s multi-booking) — hide, don't hand them an error.
              const canRegenInvoice = (userRole === 'manager'
                  || (userRole === 'supervisor' && (detail.bookings?.length ?? 0) <= 1))
                && !['paid', 'partially_paid'].includes(invStatus);
              const regenBtn = canRegenInvoice && (
                <button
                  className="invMgr__btn invMgr__btn--ghost"
                  disabled={regenBusy || isActionLoading}
                  title="Rewrite this invoice's lines from the current booking details — the invoice number and payment link stay the same"
                  onClick={regenerateInvoice}>
                  {regenBusy ? 'Updating…' : 'Update Invoice'}
                </button>
              );
              return !canEdit ? (
                <>
                  {regenBtn && <div className="invMgr__actions">{regenBtn}</div>}
                  {!!regenError && <div className="invMgr__payLinkError">{regenError}</div>}
                  <div className="invMgr__payNote" style={{ marginTop: 8 }}>
                    You have view-only access to this invoice. Ask a manager to send it or apply a payment.
                  </div>
                </>
              ) : (
                <>
                {/* Warnings BEFORE the buttons they explain (owner,
                    2026-09-02) — the reason to pick Apply Payment & Send must
                    be read before the row offers the choice. */}
                {applyUnknown && (
                  <div className="invMgr__sendBlocked invMgr__sendBlocked--lead" role="status">
                    Zoho did not load, so we cannot tell whether the client&rsquo;s wallet has
                    already paid for these bookings. Close and reopen this invoice before sending.
                  </div>
                )}
                {!applyUnknown && walletUnapplied > 0.009 && (
                  <div className="invMgr__sendBlocked invMgr__sendBlocked--lead" role="status">
                    This client has already paid AED {walletUnapplied.toFixed(2)} in advance
                    (wallet or card) for these bookings. Use <strong>Apply Payment &amp; Send</strong> —
                    sending without it would ask them to pay twice.
                  </div>
                )}
                <div className="invMgr__actions">
                  {/* Guest invoices have NO send step (2026-08-09) — the
                      payment link is the send; minting it marks them sent. */}
                  {!detail.invoice.is_guest_invoice && (
                  <button
                    className="invMgr__btn invMgr__btn--primary"
                    /* Sending is BLOCKED while the live Zoho read is missing: we
                       cannot tell whether the wallet has already paid for these
                       bookings, and guessing sent five invoices for money
                       already taken (2026-09-01). Refuse rather than guess. */
                    disabled={isActionLoading || applyUnknown}
                    title={applyUnknown
                      ? 'Zoho did not load, so the payment position is unknown. Reopen the invoice before sending.'
                      : undefined}
                    onClick={() => openSendConfirm(hasAdvance)}>
                    {/* Once an invoice has left DRAFT the client already has it,
                        so the action is a RE-send and the button must say so
                        (2026-08-30). The confirm dialog knew this; the button
                        that opens it did not, and read "Send Invoice" over an
                        invoice sent weeks ago. */}
                    {isActionLoading ? 'Working…'
                      : applyUnknown ? 'Send unavailable'
                      : hasAdvance ? 'Apply Payment & Send'
                      : (invoiceLeftDraft ? 'Re-send Invoice' : 'Send Invoice')}
                  </button>
                  )}

                  {/* Apply WITHOUT sending (owner 2026-09-23): "one apply and
                      send, and one just apply" — the client's own advance
                      (wallet or card) goes onto the invoice and nobody is
                      messaged; the pay link then shows only what is left. */}
                  {hasAdvance && !alreadyApplied && (
                    <button
                      className="invMgr__btn invMgr__btn--ghost"
                      disabled={isActionLoading || applyUnknown}
                      onClick={() => setApplyTarget({
                        id: detail.invoice.id,
                        bonus: apply.bonusToApply,
                        cash: apply.cashToApply,
                        card: apply.cardToApply,
                        wallet: apply.walletToApply,
                      })}>
                      Apply Payment
                    </button>
                  )}

                  {alreadyApplied && (
                    <button
                      className="invMgr__btn invMgr__btn--ghost"
                      disabled={isActionLoading}
                      onClick={() => setRevertTarget({
                        id: detail.invoice.id,
                        bonus: parseFloat(detail.invoice.wallet_bonus_applied || 0),
                        cash: parseFloat(detail.invoice.wallet_cash_applied || 0),
                      })}>
                      Revert Payment
                    </button>
                  )}

                  {regenBtn}

                  <button className="invMgr__btn invMgr__btn--danger"
                    onClick={() => { setDeleteTarget({ id: detail.invoice.id, zoho_invoice_number: detail.invoice.zoho_invoice_number, booking_count: detail.bookings.length }); setDeleteTyped(''); setDeleteStage('confirm'); }}>
                    Delete Invoice
                  </button>
                </div>
                {!!regenError && <div className="invMgr__payLinkError">{regenError}</div>}

                {/* The audit trail — who generated, sent, updated, applied.
                    One quiet toggle; rows load on first open. MANAGERS ONLY
                    (owner, 2026-09-03). */}
                {userRole === 'manager' && (
                <div className="invMgr__history">
                  <div className="invMgr__historyTitle">History</div>
                  {(
                    historyRows === null ? (
                      <div className="invMgr__historyRow invMgr__historyRow--muted">Loading…</div>
                    ) : historyRows.length === 0 ? (
                      <div className="invMgr__historyRow invMgr__historyRow--muted">
                        No recorded actions yet — the trail starts with changes made from now on.
                      </div>
                    ) : (
                      historyRows.map((h, i) => {
                        const d = h.new_data || {};
                        const extra = h.action === 'invoice_payment_applied' || h.action === 'invoice_payment_reverted'
                          ? ` — ${money(Number(d.cash || 0) + Number(d.bonus || 0))}`
                          : (h.action === 'invoice_created' || h.action === 'invoice_updated') && d.total != null
                            ? ` — ${money(d.total)}`
                            : '';
                        const label = h.action === 'invoice_payment_applied'
                          ? appliedLabel(d)
                          : h.action === 'invoice_updated' && d.via === 'zoho'
                            ? `Updated in Zoho${d.status ? ` (${String(d.status).split('→').map((x) => titleCase(x.trim())).join(' → ')})` : ''}`
                            : h.action === 'invoice_updated' && d.via === 'booking_edit'
                              ? `Updated from a booking edit${d.direction === 'increase' ? ' (price up)' : d.direction === 'decrease' ? ' (price down)' : d.direction === 'cancelled' ? ' (cancelled)' : ''}`
                              : (HISTORY_LABEL[h.action] || h.action);
                        return (
                          <div key={i} className="invMgr__historyRow">
                            <span>{label}{extra}</span>
                            <span className="invMgr__historyMeta">{h.by_name?.trim() || 'system'} · {fmtDT(h.changed_at)}</span>
                          </div>
                        );
                      })
                    )
                  )}
                </div>
                )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      <LoadingPopup isLoading={isActionLoading} feedbackMessage={feedbackMessage} onClose={() => setFeedbackMessage("")} />

      {/* Unified send confirm — shows the EXACT total the client will be told. */}
      <ConfirmPopup
        isOpen={!!sendConfirm}
        title={sendConfirm?.alreadySent
          ? "Send again?"
          : (sendConfirm?.applyFirst ? "Apply Payment & Send" : "Send Invoice")}
        message={(() => {
          if (sendConfirm?.loading) return "Calculating the client's total amount due…";
          const due = sendConfirm?.previewDue;
          // Fallback wording only if the preview couldn't be computed.
          const duePhrase = (due == null)
            ? "their invoice and total amount due (or a no-dues note if nothing is owed)"
            : (due > 0.009
                ? `their invoice — the client will be told their total amount due is ${money(due)}`
                : "their invoice — the client will be told they have no pending dues");
          // Re-send: this invoice is already past draft in Zoho, so warn first.
          if (sendConfirm?.alreadySent) {
            return `⚠️ This invoice has ALREADY been sent. Sending again will re-notify the client on WhatsApp — ${duePhrase}. Are you sure you want to send it again?`;
          }
          return sendConfirm?.applyFirst
            ? `This will apply ${sendConfirm?.applyWhat || "the client's advance payment"} to this invoice, then WhatsApp ${duePhrase}. Continue?`
            : `This will mark the invoice as sent and WhatsApp ${duePhrase}. Continue?`;
        })()}
        confirmLabel={(sendConfirm?.loading || isActionLoading)
          ? "Working…"
          : (sendConfirm?.alreadySent
              ? "Send again"
              : (sendConfirm?.applyFirst ? "Apply & Send" : "Send Invoice"))}
        cancelLabel="Cancel"
        isDestructive={!!sendConfirm?.alreadySent}
        // Block submit while the amount-due preview is still calculating AND
        // while the send itself is in flight (prevents a double-click double-send).
        confirmDisabled={!!sendConfirm?.loading || isActionLoading}
        onConfirm={() => doSend(!!sendConfirm?.applyFirst)}
        onCancel={() => setSendConfirm(null)}
      />

      {/* Apply (kept for parity if a host opens applyTarget directly) */}
      <ConfirmPopup
        isOpen={!!applyTarget}
        title="Apply Payment"
        message={applyTarget
          ? `Apply ${describeApply({ bonus: applyTarget.bonus, card: applyTarget.card ?? 0, wallet: applyTarget.wallet ?? applyTarget.cash }, money)} to this invoice? Nothing is sent to the client.`
          : ''}
        confirmLabel="Apply"
        cancelLabel="Cancel"
        isDestructive={false}
        onConfirm={doApplyPayment}
        onCancel={() => setApplyTarget(null)}
      />

      {/* Revert */}
      <ConfirmPopup
        isOpen={!!revertTarget}
        title="Revert Payment"
        message={revertTarget
          ? `Remove the ${money(revertTarget.bonus)} bonus discount and un-apply ${money(revertTarget.cash)} cash from this invoice? The invoice returns to draft.`
          : ''}
        confirmLabel="Revert"
        cancelLabel="Cancel"
        isDestructive={true}
        onConfirm={doRevertPayment}
        onCancel={() => setRevertTarget(null)}
      />

      {/* Delete — stage 1 */}
      <ConfirmPopup
        isOpen={deleteStage === 'confirm'}
        title="Delete Invoice"
        message={deleteTarget
          ? `Are you sure you want to delete invoice ${deleteTarget.zoho_invoice_number || `#${deleteTarget.id}`}? This will also delete it from Zoho (if it still exists) and unlink ${deleteTarget.booking_count || 0} booking(s). This action cannot be undone.`
          : ''}
        confirmLabel="Yes, continue"
        cancelLabel="Cancel"
        isDestructive={true}
        onConfirm={() => { setDeleteTyped(''); setDeleteStage('type'); }}
        onCancel={() => { setDeleteStage(null); setDeleteTarget(null); setDeleteTyped(''); }}
      />

      {/* Delete — stage 2 (type DELETE). Uses ConfirmPopup (same as every other
          confirm here) so it gets the proper white modal card — the old custom
          popup-overlay/popup-content markup rendered with no background. */}
      <ConfirmPopup
        isOpen={deleteStage === 'type'}
        title="Type DELETE to confirm"
        message={(
          <div>
            <p style={{ marginTop: 0, color: '#6b7280' }}>
              Deleting invoice {deleteTarget?.zoho_invoice_number || `#${deleteTarget?.id || ''}`} is
              permanent. Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              ref={deleteInputRef}
              type="text"
              value={deleteTyped}
              onChange={(e) => setDeleteTyped(e.target.value)}
              placeholder="Type DELETE"
              autoComplete="off"
              spellCheck={false}
              style={{ width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', letterSpacing: 0.5 }}
            />
          </div>
        )}
        confirmLabel="Delete permanently"
        cancelLabel="Cancel"
        isDestructive={true}
        confirmDisabled={deleteTyped !== 'DELETE'}
        onConfirm={() => doDelete(false)}
        onCancel={() => { setDeleteStage(null); setDeleteTarget(null); setDeleteTyped(''); }}
      />
    </div>
  );
}
